import {issueHovercardPath} from '@github-ui/paths'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLBox} from '@github-ui/safe-html/VerifiedHTML'
import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import {Link} from '@primer/react'
import {Box} from '@primer/styled-react'
import {PageHeader} from '@primer/styled-react/experimental'
import {clsx} from 'clsx'
import {useFragment} from 'react-relay'
import {graphql} from 'relay-runtime'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import type {OptionConfig} from '../OptionConfig'
import type {HeaderParentTitle$key} from './__generated__/HeaderParentTitle.graphql'
import type {HeaderViewer$key} from './__generated__/HeaderViewer.graphql'
import {HeaderMenu, type HeaderMenuBaseProps} from './HeaderMenu'
import styles from './HeaderViewer.module.css'

export type HeaderViewerProps = {
  headerViewerKey: HeaderViewer$key
  parentKey?: HeaderParentTitle$key
  optionConfig: OptionConfig
} & HeaderMenuBaseProps

export function HeaderViewer({headerViewerKey, optionConfig, containerRef, ...rest}: HeaderViewerProps) {
  const data = useFragment(
    graphql`
      fragment HeaderViewer on Issue {
        titleHTML
        number
        url
        viewerCanUpdateNext
        repository {
          name
          owner {
            login
          }
        }
        ...HeaderMenu
      }
    `,
    headerViewerKey,
  )
  const {titleHTML, number, url, repository} = data
  const {titleAs = 'h1'} = optionConfig

  const headerMenu = (
    <HeaderMenu optionConfig={optionConfig} headerMenuData={data} containerRef={containerRef} {...rest} />
  )

  const breakpoint = useContainerBreakpoint(containerRef?.current ?? null)

  // Props here correspond to only having 'Copy link' button rendered in HeaderMenu
  const areHeaderActionsMinimal =
    !data.viewerCanUpdateNext &&
    !optionConfig.showIssueCreateButton &&
    !optionConfig.additionalHeaderActions &&
    !optionConfig.customEditMenuEntries

  return (
    <div aria-label="Header" role={'region'} data-testid={TEST_IDS.issueHeader}>
      <PageHeader className={`${styles.headerContainer} ${areHeaderActionsMinimal ? styles.minimalHeader : ''}`}>
        <PageHeader.TitleArea className={styles.PageHeader_TitleArea}>
          <PageHeader.Title
            as={titleAs}
            sx={{
              // There is no design system value for '26px',
              // but we want to align with PRs experience
              fontSize: optionConfig.useViewportQueries
                ? ['26px', '26px', 'var(--text-title-size-large, 32px)', 'var(--text-title-size-large, 32px)']
                : breakpoint([
                    '26px',
                    '26px',
                    'var(--text-title-size-large, 32px)',
                    'var(--text-title-size-large, 32px)',
                  ]),

              mr: optionConfig.insideSidePanel ? undefined : 2,
            }}
            className={styles.PageHeader_Title}
          >
            <VerifiedHTMLBox
              as="bdi"
              sx={{
                mr: optionConfig.insideSidePanel ? 2 : undefined,
              }}
              className={clsx('markdown-title', styles.VerifiedHTMLBox)}
              html={titleHTML as SafeHTMLString}
              data-testid={TEST_IDS.issueTitle}
            />
            {optionConfig.insideSidePanel ? (
              <div className={styles.issueNumberContainer}>
                <Link
                  href={url}
                  inline
                  data-hovercard-url={issueHovercardPath({
                    owner: repository.owner.login,
                    repo: repository.name,
                    issueNumber: number,
                  })}
                  className={styles.issueNumberLink}
                >
                  {LABELS.issueNumber(number)}
                </Link>
              </div>
            ) : (
              <>
                &nbsp;
                <span className={styles.issueNumberText}>{LABELS.issueNumber(number)}</span>
              </>
            )}
          </PageHeader.Title>
        </PageHeader.TitleArea>
        {/* Mobile page viewport actions */}
        <PageHeader.ContextArea>
          <PageHeader.ContextAreaActions className={styles.PageHeader_ContextAreaActions}>
            {headerMenu}
          </PageHeader.ContextAreaActions>
        </PageHeader.ContextArea>
        {/* Desktop page viewport, narrow container viewport actions */}
        {!optionConfig.useViewportQueries && (
          // Only in the case where container-aware breakpoints are used
          <PageHeader.ContextArea hidden={false} className={styles.PageHeader_ContextArea}>
            <div className={styles.desktopMetadataContainer}>
              <Box
                sx={{
                  display: breakpoint(['flex', 'flex', 'none', 'none']),
                }}
                className={styles.metadataWrapper}
              >
                {headerMenu}
              </Box>
            </div>
          </PageHeader.ContextArea>
        )}
        <PageHeader.Actions
          sx={{
            display: optionConfig.useViewportQueries
              ? ['none', 'none', 'flex', 'flex']
              : breakpoint(['none', 'none', 'flex', 'flex']),
          }}
          className={styles.PageHeader_Actions}
        >
          {headerMenu}
        </PageHeader.Actions>
      </PageHeader>
    </div>
  )
}

try{ HeaderViewer.displayName ||= 'HeaderViewer' } catch {}